import { createContext, useContext, useEffect, useState } from "react";
import { apiRequest } from "@/lib/api";
import { getRefreshToken } from "@/lib/auth-storage";
import { clearAuthTokens, getAccessToken, setAuthTokens } from "@/lib/auth-storage";
import { isCrispEnabled, resetCrispSession, setCrispSessionData, setCrispUser } from "@/lib/crisp";

type AuthUser = {
  id: string;
  email: string;
  fullName: string;
  role: "ADMIN" | "PM" | "FIELD";
  companyId: string;
  workspaceStatus?: "ACTIVE" | "SUSPENDED";
  currentPlan?: "STARTER" | "PRO" | "TEAM";
  isPaying?: boolean;
  planInterval?: "MONTHLY" | "YEARLY";
  subscriptionExpiresAt?: string | null;
  subscriptionSecondsUntilExpiry?: number | null;
  subscriptionDaysRemaining?: number | null;
  subscriptionExpired?: boolean;
};

type AuthTokens = {
  accessToken: string;
  refreshToken: string;
  tokenType: "Bearer";
  expiresIn: string;
};

type AuthResponse = {
  user: AuthUser;
  tokens: AuthTokens;
};

type RegisterPayload = {
  companyName: string;
  ownerFullName: string;
  email: string;
  password: string;
  phone?: string;
  companyType?:
    | "GENERAL_CONTRACTOR_REMODELER"
    | "PLUMBING_CONTRACTOR"
    | "ELECTRICAL_CONTRACTOR"
    | "HVAC_CONTRACTOR"
    | "LOW_VOLTAGE_SECURITY_ACCESS_CONTROL_CONTRACTOR"
    | "DRYWALL_FRAMING_CARPENTRY_CONTRACTOR"
    | "FLOORING_CONTRACTOR"
    | "PAINTING_CONTRACTOR"
    | "CONCRETE_CONTRACTOR"
    | "ROOFING_CONTRACTOR"
    | "LANDSCAPING_HARDSCAPING_CONTRACTOR";
  companySize?: string;
};

type AuthContextValue = {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  completePendingSignup: (pendingSignupId: string) => Promise<void>;
  acceptInvite: (payload: { token: string; password: string; fullName?: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const isApprovalPage =
    typeof window !== "undefined" &&
    /^\/approve\/[^/]+$/.test(window.location.pathname);

  const identifyCrispUser = (nextUser: AuthUser) => {
    if (import.meta.env.VITE_DISABLE_CRISP === "true" || !isCrispEnabled()) return;
    setCrispUser({
      email: nextUser.email,
      name: nextUser.fullName,
    });
    setCrispSessionData({
      workspaceId: nextUser.companyId,
      userId: nextUser.id,
      plan: nextUser.planInterval ?? "unknown",
      environment: import.meta.env.MODE,
    });
  };

  useEffect(() => {
    const init = async () => {
      if (isApprovalPage) {
        setLoading(false);
        return;
      }

      const token = getAccessToken();
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const me = await apiRequest<AuthUser>("/auth/me", { method: "GET", auth: true });
        identifyCrispUser(me);
        setUser(me);
      } catch {
        clearAuthTokens();
        resetCrispSession();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    void init();
  }, [isApprovalPage]);

  const hydrateAuthenticatedUser = async () => {
    const me = await apiRequest<AuthUser>("/auth/me", { method: "GET", auth: true });
    identifyCrispUser(me);
    setUser(me);
  };

  useEffect(() => {
    const onExpired = () => {
      setUser((current) =>
        current
          ? {
              ...current,
              workspaceStatus: "SUSPENDED",
              subscriptionExpired: true,
              subscriptionSecondsUntilExpiry: 0,
            }
          : current,
      );
    };

    window.addEventListener("workspace-subscription-expired", onExpired);
    return () => window.removeEventListener("workspace-subscription-expired", onExpired);
  }, []);

  const login = async (email: string, password: string) => {
    let response: AuthResponse;
    try {
      response = await apiRequest<AuthResponse>("/auth/login", {
        method: "POST",
        body: { email, password },
        auth: false,
      });
    } catch {
      throw new Error("Invalid email or password");
    }

    setAuthTokens({
      accessToken: response.tokens.accessToken,
      refreshToken: response.tokens.refreshToken,
    });
    await hydrateAuthenticatedUser();
  };

  const register = async (payload: RegisterPayload) => {
    const response = await apiRequest<AuthResponse>("/auth/register", {
      method: "POST",
      body: payload,
      auth: false,
    });

    setAuthTokens({
      accessToken: response.tokens.accessToken,
      refreshToken: response.tokens.refreshToken,
    });
    await hydrateAuthenticatedUser();
  };

  const completePendingSignup = async (pendingSignupId: string) => {
    const response = await apiRequest<AuthResponse>("/auth/complete-pending-signup", {
      method: "POST",
      body: { pendingSignupId },
      auth: false,
    });

    setAuthTokens({
      accessToken: response.tokens.accessToken,
      refreshToken: response.tokens.refreshToken,
    });
    await hydrateAuthenticatedUser();
  };

  const acceptInvite = async (payload: { token: string; password: string; fullName?: string }) => {
    const response = await apiRequest<AuthResponse>("/auth/accept-invite", {
      method: "POST",
      body: payload,
      auth: false,
    });

    setAuthTokens({
      accessToken: response.tokens.accessToken,
      refreshToken: response.tokens.refreshToken,
    });
    await hydrateAuthenticatedUser();
  };

  const logout = async () => {
    try {
      await apiRequest<{ success: boolean }>("/auth/logout", {
        method: "POST",
        body: { refreshToken: getRefreshToken() ?? undefined },
      });
    } finally {
      clearAuthTokens();
      resetCrispSession();
      setUser(null);
    }
  };

  const refreshMe = async () => {
    await hydrateAuthenticatedUser();
  };

  const value: AuthContextValue = { user, loading, login, register, completePendingSignup, acceptInvite, logout, refreshMe };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
